import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileCheck,
  Hourglass,
  LogOut,
  Mail,
  MessageCircleWarning,
  RefreshCw,
  Search,
  Send,
  Settings,
  Trash2,
  Undo2,
  Upload,
  XCircle,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { Modal } from '../../components/Modal';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/format';
import { toMajor } from '../../contracts/money';
import type { ResellerViewState } from '../../mocks/reseller';
import { useApplicantContext } from '../../api/reseller';
import { useResellerApplications } from '../../api/admin';
import type { ResellerApplication } from '../../mocks/admin';

/* ──────────────────────────────────────────────────────────────────────────
 * ApplicationStatus
 *
 * Single page mounted at /become-a-reseller/status and (separately) /reseller/status.
 * Renders one of six layouts depending on `currentApplicantContext.view`.
 *
 * The page also exposes a small dev toggle so reviewers can flip through every
 * possible state without changing source — local React state shadows the
 * mock module's value (the import stays the source of truth at refresh).
 * ──────────────────────────────────────────────────────────────────────── */

const APPLICANT_STATES: Array<{ value: ResellerViewState; label: string }> = [
  { value: 'application_pending', label: 'Application — pending' },
  { value: 'application_in_review', label: 'Application — in review' },
  { value: 'application_more_info', label: 'Application — more info needed' },
  { value: 'application_rejected', label: 'Application — rejected' },
  { value: 'application_withdrawn', label: 'Application — withdrawn' },
  { value: 'pending_setup', label: 'Approved — onboarding pending' },
  { value: 'active', label: 'Active reseller (redirect)' },
];

/**
 * Resolve which mock application backs the current view. The mock has 6
 * applications in admin.ts; we pick a representative one per state so we can
 * show realistic data in every layout. If nothing matches we fall back to
 * the first application in the list.
 */
function applicationForView(
  view: ResellerViewState,
  resellerApplications: ResellerApplication[],
): ResellerApplication | undefined {
  switch (view) {
    case 'application_pending':
      return resellerApplications.find((a) => a.status === 'pending') ?? resellerApplications[0];
    case 'application_in_review':
      return resellerApplications.find((a) => a.status === 'in_review') ?? resellerApplications[0];
    case 'application_more_info':
      return resellerApplications.find((a) => a.status === 'more_info_needed') ?? resellerApplications[0];
    case 'application_rejected':
      return resellerApplications.find((a) => a.status === 'rejected') ?? resellerApplications[0];
    case 'application_withdrawn':
      return resellerApplications.find((a) => a.status === 'withdrawn') ?? resellerApplications[0];
    case 'pending_setup':
      return resellerApplications.find((a) => a.status === 'approved') ?? resellerApplications[0];
    default:
      return resellerApplications[0];
  }
}

export function ApplicationStatus() {
  const { data: currentApplicantContext } = useApplicantContext();
  const { data: resellerApplications = [] } = useResellerApplications();
  // Local override of the fetched view — lets the demo selector flip layouts.
  const [view, setView] = useState<ResellerViewState>('application_pending');
  const application = useMemo(
    () => applicationForView(view, resellerApplications),
    [view, resellerApplications],
  );

  // Seed the view from the applicant context once it lands.
  useEffect(() => {
    if (currentApplicantContext) setView(currentApplicantContext.view);
  }, [currentApplicantContext]);

  if (!application) {
    return (
      <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid place-items-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark px-4 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto">
        {/* Brand header */}
        <div className="flex flex-col items-center mb-7">
          <Logo className="w-10 h-10 rounded-xl" />
          <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            Possignin — partner program
          </p>
        </div>

        {/* Demo state-switcher (mock-mode only — always shown here) */}
        <DemoStateSelector value={view} onChange={setView} />

        {view === 'application_pending' && (
          <PendingLayout application={application} onWithdraw={() => setView('application_withdrawn')} />
        )}
        {view === 'application_in_review' && (
          <InReviewLayout application={application} onWithdraw={() => setView('application_withdrawn')} />
        )}
        {view === 'application_more_info' && (
          <MoreInfoLayout application={application} onSent={() => setView('application_in_review')} onWithdraw={() => setView('application_withdrawn')} />
        )}
        {view === 'application_rejected' && (
          <RejectedLayout application={application} />
        )}
        {view === 'application_withdrawn' && (
          <WithdrawnLayout application={application} />
        )}
        {view === 'pending_setup' && (
          <PendingSetupLayout application={application} />
        )}
        {view === 'active' && (
          <ActiveStateNotice />
        )}

        {/* Common footer */}
        <Footer />
      </div>
    </div>
  );
}

/* ── Demo selector ───────────────────────────────────────────────────────── */

function DemoStateSelector({
  value,
  onChange,
}: {
  value: ResellerViewState;
  onChange: (v: ResellerViewState) => void;
}) {
  return (
    <div className="mb-5 flex items-center gap-3 px-3 py-2 rounded-md bg-zinc-100/70 dark:bg-zinc-800/40 border border-dashed border-zinc-300 dark:border-zinc-700 text-xs text-zinc-600 dark:text-zinc-300">
      <span className="font-semibold uppercase tracking-wider text-[10px]">Demo</span>
      <span className="hidden sm:inline">simulate state</span>
      <div className="ml-auto relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as ResellerViewState)}
          aria-label="Simulate application state"
          className="appearance-none px-3 py-1.5 pr-8 rounded-md text-xs bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-50 border border-zinc-200 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm"
        >
          {APPLICANT_STATES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-500 dark:text-zinc-400" />
      </div>
    </div>
  );
}

/* ── Application progress strip ──────────────────────────────────────────── */

type StepKey = 'submitted' | 'review' | 'decision' | 'setup';

function ProgressStrip({ current }: { current: StepKey }) {
  const order: StepKey[] = ['submitted', 'review', 'decision', 'setup'];
  const idx = order.indexOf(current);

  const steps: Array<{ key: StepKey; label: string; icon: React.ReactNode }> = [
    { key: 'submitted', label: 'Submitted', icon: <FileCheck className="w-4 h-4" /> },
    { key: 'review', label: 'Under review', icon: <Search className="w-4 h-4" /> },
    { key: 'decision', label: 'Decision', icon: <Mail className="w-4 h-4" /> },
    { key: 'setup', label: 'Setup', icon: <Settings className="w-4 h-4" /> },
  ];

  return (
    <ol className="grid grid-cols-4 gap-2 sm:gap-3" aria-label="Application progress">
      {steps.map((step, i) => {
        const state = i < idx ? 'done' : i === idx ? 'current' : 'upcoming';
        return (
          <li key={step.key} className="flex flex-col items-center text-center gap-1.5">
            <span
              className={cn(
                'w-9 h-9 grid place-items-center rounded-full transition relative',
                state === 'done' && 'bg-emerald-600 text-white',
                state === 'current' && 'bg-amber-500 text-white',
                state === 'upcoming' && 'bg-zinc-200 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400',
              )}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {state === 'current' && step.key === 'review' && (
                <span className="absolute inset-0 rounded-full bg-amber-500/40 animate-ping" aria-hidden="true" />
              )}
              <span className="relative">{step.icon}</span>
            </span>
            <span
              className={cn(
                'text-[11px] sm:text-xs font-medium leading-tight',
                state === 'done' && 'text-emerald-700 dark:text-emerald-300',
                state === 'current' && 'text-amber-700 dark:text-amber-300',
                state === 'upcoming' && 'text-zinc-500 dark:text-zinc-400',
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/* ── Pending layout ──────────────────────────────────────────────────────── */

function PendingLayout({
  application,
  onWithdraw,
}: {
  application: ResellerApplication;
  onWithdraw: () => void;
}) {
  return (
    <Card className="p-6 sm:p-8 border-l-4 border-l-amber-400 dark:border-l-amber-500">
      <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex-shrink-0">
          <Hourglass className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
            Application submitted
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            We've got your application
          </h1>
          <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            We'll respond within 1–2 business days. We'll email{' '}
            <span className="mono text-zinc-800 dark:text-zinc-100">{application.email}</span> as
            soon as a decision is made.
          </p>
        </div>
      </div>

      <ApplicationMetaBlock application={application} />

      <div className="my-6">
        <ProgressStrip current="review" />
      </div>

      <QuickActions
        application={application}
        onWithdraw={onWithdraw}
      />
    </Card>
  );
}

/* ── In-review layout ────────────────────────────────────────────────────── */

function InReviewLayout({
  application,
  onWithdraw,
}: {
  application: ResellerApplication;
  onWithdraw: () => void;
}) {
  return (
    <Card className="p-6 sm:p-8 border-l-4 border-l-amber-400 dark:border-l-amber-500">
      <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex-shrink-0">
          <Search className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
            Under review
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            An admin is reviewing your application
          </h1>
          {application.reviewedBy ? (
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
              Being reviewed by{' '}
              <span className="mono text-zinc-800 dark:text-zinc-100">{application.reviewedBy}</span>.
              Usually decided within 24 hours of pickup.
            </p>
          ) : (
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
              Usually decided within 24 hours of pickup.
            </p>
          )}
        </div>
      </div>

      <ApplicationMetaBlock application={application} />

      <div className="my-6">
        <ProgressStrip current="review" />
      </div>

      <QuickActions
        application={application}
        onWithdraw={onWithdraw}
      />
    </Card>
  );
}

/* ── More-info layout ────────────────────────────────────────────────────── */

function MoreInfoLayout({
  application,
  onSent,
  onWithdraw,
}: {
  application: ResellerApplication;
  onSent: () => void;
  onWithdraw: () => void;
}) {
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  function pickFile() {
    // Mock-only: append a fake filename
    const fakeNames = ['registration.pdf', 'reference-letter.pdf', 'invoice-portfolio.pdf'];
    const next = fakeNames[files.length % fakeNames.length]!;
    setFiles((f) => [...f, next]);
  }

  function removeFile(name: string) {
    setFiles((f) => f.filter((n) => n !== name));
  }

  function send() {
    if (!reply.trim() && files.length === 0) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSuccess(true);
      setTimeout(onSent, 800);
    }, 600);
  }

  return (
    <Card className="p-6 sm:p-8 border-l-4 border-l-amber-400 dark:border-l-amber-500">
      <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex-shrink-0">
          <MessageCircleWarning className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
            More information needed
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            We need a bit more from you
          </h1>
        </div>
      </div>

      <Banner tone="warning" className="mb-5" title="Your application is on hold">
        {application.decisionReason ?? 'Please provide additional details before we can continue.'}
      </Banner>

      <ApplicationMetaBlock application={application} />

      <div className="my-6">
        <ProgressStrip current="review" />
      </div>

      {/* Reply form */}
      <div className="space-y-4">
        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Send a reply</div>

        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={5}
          placeholder="Type your response here, attach links to documents, etc."
          className="block w-full px-3 py-2.5 text-sm rounded-md bg-white text-zinc-950 placeholder-zinc-400 border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder-zinc-500 dark:border-zinc-700 dark:focus:ring-brand-dm dark:focus:border-brand-dm"
        />

        {/* File upload mock */}
        <button
          type="button"
          onClick={pickFile}
          className={cn(
            'w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed cursor-pointer transition',
            'border-zinc-300 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-600',
            'bg-zinc-50/50 dark:bg-zinc-900/40',
          )}
        >
          <span className="w-9 h-9 grid place-items-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400">
            <Upload className="w-4 h-4" />
          </span>
          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
            Drag files here or click to browse
          </span>
          <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
            PDF, JPG, PNG · up to 10 MB each
          </span>
        </button>

        {files.length > 0 && (
          <ul className="space-y-1.5">
            {files.map((name) => (
              <li
                key={name}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 text-sm"
              >
                <FileCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="mono truncate flex-1 text-zinc-700 dark:text-zinc-200">{name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(name)}
                  className="text-zinc-400 hover:text-rose-600 transition"
                  aria-label={`Remove ${name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {success && (
          <Banner tone="success" title="Reply sent">
            Admin will review and respond shortly.
          </Banner>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onWithdraw}
            iconLeft={<Undo2 className="w-3.5 h-3.5" />}
          >
            Withdraw application
          </Button>
          <Button
            onClick={send}
            loading={submitting}
            disabled={success || (!reply.trim() && files.length === 0)}
            iconRight={!submitting && !success ? <Send className="w-4 h-4" /> : undefined}
          >
            {success ? 'Sent' : 'Send reply'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ── Rejected layout ─────────────────────────────────────────────────────── */

function RejectedLayout({
  application,
}: {
  application: ResellerApplication;
}) {
  const [rebuttalOpen, setRebuttalOpen] = useState(false);

  // Cooldown calculation
  const reapplyDate = application.reapplyAfter ? new Date(application.reapplyAfter) : null;
  const today = new Date();
  const canReapplyNow = reapplyDate ? today >= reapplyDate : true;
  const daysUntil = reapplyDate
    ? Math.max(0, Math.ceil((reapplyDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <Card className="p-6 sm:p-8 border-l-4 border-l-rose-400 dark:border-l-rose-500">
      <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 flex-shrink-0">
          <XCircle className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-rose-700 dark:text-rose-300">
            Decision sent
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Your application wasn't approved this time
          </h1>
          <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            We've sent the full decision to{' '}
            <span className="mono text-zinc-800 dark:text-zinc-100">{application.email}</span>.
          </p>
        </div>
      </div>

      <ApplicationMetaBlock application={application} />

      {application.decisionReason && (
        <div className="mt-5 rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/30 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300 mb-1.5">
            Reason
          </div>
          <p className="text-sm text-zinc-800 dark:text-zinc-200 leading-relaxed">
            {application.decisionReason}
          </p>
        </div>
      )}

      {/* Cooldown card */}
      <div className="mt-5">
        {canReapplyNow ? (
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/30 p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                You can re-apply now.
              </div>
              <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                Your cooldown has ended. We'd recommend addressing the reason above before reapplying.
              </p>
            </div>
            <a
              href="/become-a-reseller"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition flex-shrink-0"
            >
              Re-apply
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/30 p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-50 tabular-nums">
                You can re-apply in {daysUntil} {daysUntil === 1 ? 'day' : 'days'}, on{' '}
                {reapplyDate ? formatDate(reapplyDate) : '—'}.
              </div>
              <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                We use a cooldown so applicants have time to address the feedback above.
              </p>
            </div>
            <button
              type="button"
              disabled
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500 cursor-not-allowed flex-shrink-0"
              title={`Available on ${reapplyDate ? formatDate(reapplyDate) : '—'}`}
            >
              Re-apply
            </button>
          </div>
        )}
      </div>

      {/* Empathetic copy */}
      <div className="mt-5 px-1 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        <p>
          Resellers go through a vetting process to protect customers. If your situation has
          changed materially since your last application, please re-apply when the window opens.
        </p>
      </div>

      <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setRebuttalOpen(true)}
          className="text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:underline underline-offset-2"
        >
          Was this a mistake? Send a rebuttal →
        </button>
      </div>

      <RebuttalModal open={rebuttalOpen} onClose={() => setRebuttalOpen(false)} />
    </Card>
  );
}

function RebuttalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setMessage('');
      setSubmitting(false);
      setDone(false);
    }
  }, [open]);

  function send() {
    if (!name.trim() || !message.trim()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
    }, 600);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send a rebuttal"
      footer={
        done ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={send} loading={submitting} disabled={!name.trim() || !message.trim()}>
              Send rebuttal
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-zinc-950 dark:text-zinc-50">Sent.</div>
              <p className="mt-1">We'll review your message and reply within 3 business days.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            Tell us what we got wrong. We read every rebuttal and reply within 3 business days.
          </p>
          <Input label="Your name" required value={name} onChange={(e) => setName(e.target.value)} />
          <label className="block">
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              Message <span className="text-rose-600 ml-0.5">*</span>
            </span>
            <textarea
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What in the decision feels wrong? What changed?"
              className="block w-full px-3 py-2.5 text-sm rounded-md bg-white text-zinc-950 placeholder-zinc-400 border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder-zinc-500 dark:border-zinc-700 dark:focus:ring-brand-dm dark:focus:border-brand-dm"
            />
          </label>
        </div>
      )}
    </Modal>
  );
}

/* ── Withdrawn layout ────────────────────────────────────────────────────── */

function WithdrawnLayout({
  application,
}: {
  application: ResellerApplication;
}) {
  // Pull the most recent "withdrew" event from the timeline if present
  const withdrewEvent = application.timeline.find((t) => /withdrew|withdraw/i.test(t.text));
  const withdrewAt = withdrewEvent?.at ?? application.submittedAt;

  return (
    <Card className="p-6 sm:p-8">
      <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
          <Undo2 className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Withdrawn
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Application withdrawn
          </h1>
          <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            You pulled this application on{' '}
            <span className="font-medium text-zinc-800 dark:text-zinc-100">{formatDate(withdrewAt)}</span>.
            You're welcome to re-apply at any time.
          </p>
        </div>
      </div>

      <ApplicationMetaBlock application={application} />

      <div className="mt-6">
        <a
          href="/become-a-reseller"
          className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 rounded-md text-base font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
        >
          Apply again
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </Card>
  );
}

/* ── Pending-setup layout ────────────────────────────────────────────────── */

function PendingSetupLayout({
  application,
}: {
  application: ResellerApplication;
}) {
  const SETUP_STEPS = ['Organization', 'Branding', 'Domain', 'Support', 'Invite staff'];

  return (
    <Card className="p-6 sm:p-8 border-l-4 border-l-emerald-400 dark:border-l-emerald-500">
      <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex-shrink-0">
          <CheckCircle2 className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            Approved
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            You're approved as a reseller
          </h1>
          <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            Almost there — finish your reseller setup to start signing customers up under{' '}
            <span className="font-medium text-zinc-800 dark:text-zinc-100">{application.orgName}</span>.
          </p>
        </div>
      </div>

      <ApplicationMetaBlock application={application} />

      {/* Setup sub-steps */}
      <div className="my-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-3">
          Setup progress
        </div>
        <ol className="grid grid-cols-5 gap-1.5 sm:gap-2.5" aria-label="Setup progress">
          {SETUP_STEPS.map((label, i) => {
            const inProgress = i === 0;
            return (
              <li key={label} className="flex flex-col items-center text-center gap-1.5">
                <span
                  className={cn(
                    'w-7 h-7 grid place-items-center rounded-full text-[11px] font-semibold tabular-nums',
                    inProgress
                      ? 'bg-emerald-600 text-white ring-2 ring-emerald-300 dark:ring-emerald-700'
                      : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400',
                  )}
                >
                  {i + 1}
                </span>
                <span
                  className={cn(
                    'text-[10px] sm:text-xs font-medium leading-tight',
                    inProgress
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-zinc-500 dark:text-zinc-400',
                  )}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-zinc-200 dark:border-zinc-700">
        <Link
          to="/login"
          className="text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 inline-flex items-center gap-1.5"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </Link>
        <Link
          to="/reseller/onboarding"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
        >
          Continue setup
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Card>
  );
}

/* ── Active state notice (defensive — should redirect via shell banner) ──── */

function ActiveStateNotice() {
  return (
    <Card className="p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <span className="w-10 h-10 grid place-items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex-shrink-0">
          <CheckCircle2 className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Your reseller account is active
          </h1>
          <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            There's nothing pending here. Head back to your reseller dashboard.
          </p>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Link
          to="/reseller"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
        >
          Go to dashboard
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Card>
  );
}

/* ── Shared sub-pieces ───────────────────────────────────────────────────── */

function ApplicationMetaBlock({
  application,
}: {
  application: ResellerApplication;
}) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Stat label="Application ID" value={<span className="mono">{application.id}</span>} />
      <Stat label="Submitted" value={formatDate(application.submittedAt)} />
      <Stat label="Org name" value={application.orgName} />
      <Stat label="Email" value={<span className="mono break-all">{application.email}</span>} />
    </dl>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 px-4 py-3">
      <dt className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">{value}</dd>
    </div>
  );
}

function QuickActions({
  application,
  onWithdraw,
}: {
  application: ResellerApplication;
  onWithdraw: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [showSubmission, setShowSubmission] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const refreshTimer = useRef<number | null>(null);

  function checkAgain() {
    setRefreshing(true);
    if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => setRefreshing(false), 600);
  }

  useEffect(() => {
    return () => {
      if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    };
  }, []);

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-2">
        <button
          type="button"
          onClick={checkAgain}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-200 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          {refreshing ? 'Checking…' : 'Check again'}
        </button>
        <button
          type="button"
          onClick={() => setShowSubmission((s) => !s)}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-200 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition"
          aria-expanded={showSubmission}
        >
          <FileCheck className="w-4 h-4" />
          {showSubmission ? 'Hide submission' : 'View what you submitted'}
        </button>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition"
        >
          <Undo2 className="w-4 h-4" />
          Withdraw
        </button>
      </div>

      {showSubmission && (
        <ReadOnlySubmission application={application} />
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Withdraw application?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmOpen(false);
                onWithdraw();
              }}
            >
              Withdraw application
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
          Are you sure? This can't be undone. You'll have to submit a new application from scratch
          if you change your mind.
        </p>
      </Modal>
    </div>
  );
}

function ReadOnlySubmission({
  application,
}: {
  application: ResellerApplication;
}) {
  return (
    <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-800/30 p-4 sm:p-5 text-sm space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        What you submitted
      </div>
      <Field label="Applicant" value={application.applicantName} />
      <Field label="Email" value={<span className="mono break-all">{application.email}</span>} />
      <Field label="Phone" value={<span className="mono">{application.phone}</span>} />
      <Field label="Country" value={application.country} />
      <Field label="Organization" value={application.orgName} />
      {application.orgWebsite && (
        <Field
          label="Website"
          value={
            <a
              href={application.orgWebsite}
              target="_blank"
              rel="noreferrer"
              className="mono underline underline-offset-2 hover:text-brand dark:hover:text-brand-dm break-all"
            >
              {application.orgWebsite}
            </a>
          }
        />
      )}
      <Field label="Years in business" value={application.yearsInBusiness} />
      <Field
        label="Expected monthly volume"
        value={<span className="tabular-nums">৳{toMajor(application.expectedVolumeMinor).toLocaleString('en-US')}</span>}
      />
      <Field label="Why us" value={<span className="block whitespace-pre-wrap leading-relaxed">{application.whyUs}</span>} />
      {application.references && (
        <Field
          label="References"
          value={<span className="block whitespace-pre-wrap leading-relaxed">{application.references}</span>}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 sm:gap-3">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 sm:pt-0.5">
        {label}
      </div>
      <div className="text-sm text-zinc-900 dark:text-zinc-100">{value}</div>
    </div>
  );
}

function Footer() {
  return (
    <div className="mt-8 flex items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
      <span>
        Need help?{' '}
        <a
          href="mailto:partner@itcitysoft.com"
          className="mono text-zinc-700 dark:text-zinc-200 hover:underline"
        >
          partner@itcitysoft.com
        </a>
      </span>
      <Link
        to="/login"
        className="inline-flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-200"
      >
        <LogOut className="w-3.5 h-3.5" /> Sign out
      </Link>
    </div>
  );
}
